import { Loader2Icon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import type { CreateSessionParams } from '~entities/session/api/create-session';

import { DateTimePicker } from '~shared/components/date-time-picker';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~shared/shadcn/ui/form';
import { Input } from '~shared/shadcn/ui/input';
import { Switch } from '~shared/shadcn/ui/switch';
import {
  formatDateAsISO,
  formatDateIgnoreTimezone,
} from '~shared/utils/date-time';
import { zodResolverWithLogging } from '~shared/utils/zod-resolver-with-logging';

export const SESSION_END_BEFORE_START_MESSAGE =
  'End date cannot be before the start date.';

export const SESSION_START_AFTER_END_MESSAGE =
  'Start date cannot be after the end date.';

type SessionDateField = 'startDate' | 'endDate';

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const sessionFormFields = z.object({
  sessionName: z.string().min(2, {
    message: 'Session name must be at least 2 characters.',
  }),
  startDate: z.date({
    required_error: 'Start date is required.',
  }),
  endDate: z.date({
    required_error: 'End date is required.',
  }),
  isRegistrationFeeRequired: z.boolean().default(false),
  defaultEvents: z.boolean().default(false),
  defaultClasses: z.boolean().default(false),
  visibleInParentPortal: z.boolean().default(true),
  registrationFee: z.number().optional(),
  registrationFeePerStudent: z.number().optional(),
  billingDate: z.date(),
  chargeLastMonthAtCheckout: z.boolean().default(false),
  chargeFirstMonthHourlyAtCheckout: z.boolean().default(false),
  chargeSessionFeesAtEnrollment: z.boolean().default(false),
});

/**
 * The invalid-range error is reported on the date field the user last touched,
 * so the message appears next to the picker they are interacting with.
 */
export const createSessionFormSchema = (
  getInvalidRangeField: () => SessionDateField = () => 'endDate',
) =>
  sessionFormFields.superRefine(({ startDate, endDate }, ctx) => {
    if (startOfDay(endDate) >= startOfDay(startDate)) return;

    const field = getInvalidRangeField();

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: [field],
      message:
        field === 'startDate'
          ? SESSION_START_AFTER_END_MESSAGE
          : SESSION_END_BEFORE_START_MESSAGE,
    });
  });

export const sessionFormSchema = createSessionFormSchema();

export interface SessionFormProps {
  handleSubmit: (value: CreateSessionParams) => Promise<void>;
  isPending: boolean;
  buttonContent?: React.ReactNode;
  defaultValues?: {
    sessionName: string;
    startDate: string;
    endDate: string;
    isRegistrationFeeRequired?: boolean;
    registrationFee?: number;
    isClassDefault?: boolean;
    isEventDefault?: boolean;
    billingDate?: string;
    registrationFeePerStudent?: Record<string | number, number>;
    visibleInParentPortal?: boolean;
    chargeLastMonthAtCheckout?: boolean;
    chargeFirstMonthHourlyAtCheckout?: boolean;
    chargeSessionFeesAtEnrollment?: boolean;
  };
}

export function SessionForm({
  handleSubmit,
  isPending,
  defaultValues,
  buttonContent,
}: SessionFormProps) {
  const [isRegistrationFeeRequired, setIsRegistrationFeeRequired] =
    useState(false);
  const [isPerStudentFee, setIsPerStudentFee] = useState(false);
  const [flatFee, setFlatFee] = useState<number>(0);
  const [perStudentFees, setPerStudentFees] = useState<Record<number, number>>(
    {},
  );

  const invalidRangeFieldRef = useRef<SessionDateField>('endDate');
  const schema = useMemo(
    () => createSessionFormSchema(() => invalidRangeFieldRef.current),
    [],
  );

  const form = useForm<z.infer<typeof sessionFormSchema>>({
    resolver: zodResolverWithLogging(schema),
    defaultValues: {
      sessionName: defaultValues?.sessionName ?? '',
      isRegistrationFeeRequired:
        defaultValues?.isRegistrationFeeRequired ?? false,
      startDate: defaultValues?.startDate
        ? new Date(formatDateIgnoreTimezone(defaultValues.startDate))
        : new Date(),
      endDate: defaultValues?.endDate
        ? new Date(formatDateIgnoreTimezone(defaultValues.endDate))
        : new Date(),
      registrationFee: defaultValues?.registrationFee ?? 0,
      defaultClasses: defaultValues?.isClassDefault ?? false,
      defaultEvents: defaultValues?.isEventDefault ?? false,
      visibleInParentPortal: defaultValues?.visibleInParentPortal ?? true,
      billingDate: defaultValues?.billingDate
        ? new Date(formatDateIgnoreTimezone(defaultValues.billingDate))
        : new Date(),
      chargeLastMonthAtCheckout:
        defaultValues?.chargeLastMonthAtCheckout ?? false,
      chargeFirstMonthHourlyAtCheckout:
        defaultValues?.chargeFirstMonthHourlyAtCheckout ?? false,
      chargeSessionFeesAtEnrollment:
        defaultValues?.chargeSessionFeesAtEnrollment ?? false,
    },
  });

  useEffect(() => {
    if (defaultValues) {
      setIsRegistrationFeeRequired(
        defaultValues.isRegistrationFeeRequired ?? false,
      );
      // Only set per-student fee state if it actually exists and has meaningful values
      if (defaultValues.registrationFeePerStudent) {
        // Backend sends object format: { "1": 10 } or { 1: 10 }
        const perStudentFeesObject = Object.fromEntries(
          Object.entries(defaultValues.registrationFeePerStudent).map(
            ([key, value]) => [Number(key), Number(value)],
          ),
        );

        // Check if we have meaningful fees
        if (Object.values(perStudentFeesObject).some((fee) => fee > 0)) {
          setPerStudentFees(perStudentFeesObject);

          // If it's a flat fee (only has key "1"), also set the flatFee state for the UI
          if (
            Object.keys(perStudentFeesObject).length === 1 &&
            perStudentFeesObject[1]
          ) {
            setFlatFee(perStudentFeesObject[1]);
          }

          setIsPerStudentFee(true);
        } else {
          // Reset to initial state when no meaningful data
          setPerStudentFees({});
          setFlatFee(0);
          setIsPerStudentFee(false);
        }
      } else {
        // Reset to initial state when no registrationFeePerStudent data
        setPerStudentFees({});
        setFlatFee(0);
        setIsPerStudentFee(false);
      }
    } else {
      // When no defaultValues, ensure clean initial state
      setPerStudentFees({});
      setFlatFee(0);
      setIsPerStudentFee(false);
      setIsRegistrationFeeRequired(false);
    }
  }, [defaultValues]);

  const startDate = form.watch('startDate');

  const handleDateRangeChange = (field: SessionDateField) => {
    invalidRangeFieldRef.current = field;
    void form.trigger(['startDate', 'endDate']);
  };

  async function onSubmit(values: z.infer<typeof sessionFormSchema>) {
    // Only send perStudentFees if the checkbox is checked AND there are actual fees
    const registrationFeePerStudent =
      isPerStudentFee && Object.values(perStudentFees).some((fee) => fee > 0)
        ? perStudentFees
        : {};

    await handleSubmit({
      name: values.sessionName,
      startDate: formatDateAsISO(values.startDate),
      endDate: formatDateAsISO(values.endDate),
      isRegistrationFee: values.isRegistrationFeeRequired,
      registrationFeeAmount: values.registrationFee,
      isClassDefault: values.defaultClasses,
      isEventDefault: values.defaultEvents,
      visibleInParentPortal: values.visibleInParentPortal,
      billingDate: formatDateAsISO(values.billingDate),
      registrationFeePerStudent,
      chargeLastMonthAtCheckout: values.chargeLastMonthAtCheckout,
      chargeFirstMonthHourlyAtCheckout: values.chargeFirstMonthHourlyAtCheckout,
      chargeSessionFeesAtEnrollment: values.chargeSessionFeesAtEnrollment,
    });
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <FormField
          control={form.control}
          name='sessionName'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Session Name</FormLabel>
              <FormControl>
                <Input placeholder='Enter session name' {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className='grid gap-4 sm:grid-cols-2'>
          <FormField
            control={form.control}
            name='startDate'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Start Date</FormLabel>
                <DateTimePicker
                  hourCycle={12}
                  granularity='day'
                  displayFormat={{ hour12: 'PPP' }}
                  value={field.value}
                  onChange={(date) => {
                    field.onChange(date);
                    handleDateRangeChange('startDate');
                  }}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='endDate'
            render={({ field }) => {
              return (
                <FormItem>
                  <FormLabel>End Date</FormLabel>
                  <DateTimePicker
                    hourCycle={12}
                    granularity='day'
                    displayFormat={{ hour12: 'PPP' }}
                    value={field.value}
                    disabledDays={{ before: startDate }}
                    onChange={(date) => {
                      field.onChange(date);
                      handleDateRangeChange('endDate');
                    }}
                  />
                  <FormMessage />
                </FormItem>
              );
            }}
          />
        </div>
        <FormField
          control={form.control}
          name='defaultClasses'
          render={({ field }) => (
            <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                  }}
                />
              </FormControl>
              <div className='space-y-1 leading-none'>
                <FormLabel>Set as class default</FormLabel>
                <FormDescription>
                  Check this if you want to make this session default for
                  classes.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='defaultEvents'
          render={({ field }) => (
            <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                  }}
                />
              </FormControl>
              <div className='space-y-1 leading-none'>
                <FormLabel>Set as event default</FormLabel>
                <FormDescription>
                  Check this if you want to make this session default for
                  events.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='isRegistrationFeeRequired'
          render={({ field }) => (
            <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                    setIsRegistrationFeeRequired(checked as boolean);
                  }}
                />
              </FormControl>
              <div className='space-y-1 leading-none'>
                <FormLabel>Is registration fee per family required?</FormLabel>
                <FormDescription>
                  Check this if you want to charge a registration fee per
                  family.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
        {isRegistrationFeeRequired && (
          <FormField
            control={form.control}
            name='registrationFee'
            shouldUnregister
            render={() => (
              <FormItem>
                <FormLabel>Registration Fee Per Family</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    placeholder='Enter registration fee per family'
                    {...form.register('registrationFee', {
                      shouldUnregister: true,
                      valueAsNumber: true,
                    })}
                  />
                </FormControl>
                <FormDescription>
                  Enter the registration fee amount per family.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className='flex flex-row items-start space-x-3 space-y-0'>
          <Checkbox
            checked={isPerStudentFee}
            onCheckedChange={(checked) => {
              setIsPerStudentFee(checked as boolean);
              if (!checked) {
                setPerStudentFees({});
                setFlatFee(0);
              }
            }}
          />
          <div className='space-y-1 leading-none'>
            <label className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
              Is registration fee per student required?
            </label>
            <p className='text-sm text-muted-foreground'>
              Check this if you want to charge a registration fee per student.
            </p>
          </div>
        </div>
        {isPerStudentFee && (
          <FormField
            control={form.control}
            name='registrationFeePerStudent'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Registration Fee Per Student</FormLabel>
                <FormControl>
                  <Input
                    type='number'
                    placeholder='Enter registration fee per student'
                    value={flatFee === 0 ? '' : flatFee}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === '' || /^\d*$/.test(val)) {
                        const amount = val === '' ? 0 : Number(val);
                        setFlatFee(amount);
                        field.onChange(amount);
                        // Update perStudentFees for backend
                        if (amount > 0) {
                          setPerStudentFees({ 1: amount });
                        } else {
                          setPerStudentFees({});
                        }
                      }
                    }}
                  />
                </FormControl>
                <FormDescription>
                  Enter the registration fee amount per student.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <div className='space-y-3 rounded-lg border p-4'>
          <div>
            <p className='text-sm font-medium'>Hourly enrollment timing</p>
            <p className='text-xs text-muted-foreground'>
              Applies to hourly classes in this session. Last month and first
              month are separate charges.
            </p>
          </div>
          <FormField
            control={form.control}
            name='chargeFirstMonthHourlyAtCheckout'
            render={({ field }) => (
              <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                    }}
                  />
                </FormControl>
                <div className='space-y-1 leading-none'>
                  <FormLabel>Charge first month hourly at checkout</FormLabel>
                  <FormDescription>
                    Collect first-month hourly tuition (full band or schedule
                    delta) at enrollment.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='chargeLastMonthAtCheckout'
            render={({ field }) => (
              <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      field.onChange(checked);
                    }}
                  />
                </FormControl>
                <div className='space-y-1 leading-none'>
                  <FormLabel>Charge last month tuition at checkout</FormLabel>
                  <FormDescription>
                    Collect last-month hourly tuition as a deposit at
                    registration.
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name='chargeSessionFeesAtEnrollment'
          render={({ field }) => (
            <FormItem className='flex flex-row items-start space-x-3 space-y-0'>
              <FormControl>
                <Checkbox
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    field.onChange(checked);
                  }}
                />
              </FormControl>
              <div className='space-y-1 leading-none'>
                <FormLabel>
                  Charge session registration fees at enrollment
                </FormLabel>
                <FormDescription>
                  When enabled, session registration fees are charged at
                  enrollment while tuition and class fees follow the session
                  billing date.
                </FormDescription>
              </div>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='billingDate'
          render={({ field }) => (
            <FormItem>
              <FormLabel>Billing Date</FormLabel>
              <FormControl>
                <DateTimePicker
                  hourCycle={12}
                  granularity='day'
                  displayFormat={{ hour12: 'PPP' }}
                  value={field.value}
                  onChange={(date) => {
                    field.onChange(date);
                  }}
                />
              </FormControl>
              <FormDescription>Date when billing occurs</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name='visibleInParentPortal'
          render={({ field }) => (
            <FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
              <div className='space-y-0.5'>
                <FormLabel>Visible in parent portal</FormLabel>
                <FormDescription>
                  When enabled, this session and its classes are visible to
                  parents in the parent portal.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <Button type='submit' disabled={isPending}>
          {isPending && <Loader2Icon className='animate-spin' />}
          {buttonContent ?? 'Create Session'}
        </Button>
      </form>
    </Form>
  );
}
